import { useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Link, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import logo from '../assets/images/logo.png';
import {
  Home, LogIn, UserPlus, LayoutDashboard, Clock, Users,
  Settings,  LogOut,  CalendarPlus, CalendarCheck, AlertTriangle, Clipboard, CreditCard, ClipboardList, Lightbulb,Star,
} from 'lucide-react';
import { urlimage } from '../utils/config';
import NotificationBell from '../components/NotificationBell';

function Navbar() {
  const { logoutUser, user } = useContext(AuthContext);
  const location = useLocation();
  const token = localStorage.getItem("authTokens");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); 
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const hasPermission = (permissionCode) => {
    return user?.is_super_admin || user?.permissions?.includes(permissionCode);
  };

  const UserDropdown = () => (
    <li className="nav-item dropdown">
      <span
        className="nav-link dropdown-toggle text-white fw-semibold d-flex align-items-center gap-2"
        id="userDropdown"
        role="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        style={{ 
          cursor: "pointer",
          transition: "background-color 0.3s",
          borderRadius: "5px",
          padding: "5px 10px"
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
      >
        <div style={{
          width: "30px",
          height: "30px",
          borderRadius: "50%",
          overflow: "hidden",
          border: "2px solid white"
        }}>
          <img
            src={
              user.image?.startsWith('/media/')
                ? `${urlimage}${user.image}`
                : `${urlimage}/media/${user.image}`
            }
            alt="Profil"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
        {user.username}
      </span>
      <ul 
        className="dropdown-menu dropdown-menu-end" 
        aria-labelledby="userDropdown" 
        style={{ 
          backgroundColor: "#1B019B",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <li>
          <Link 
            className="dropdown-item text-white d-flex align-items-center gap-2" 
            to="/modifier-compte"
            style={{ 
              backgroundColor: "transparent",
              transition: "background-color 0.3s",
              padding: "8px 16px"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
          >
            <UserPlus size={18} /> Modifier mon compte
          </Link>
        </li>
        <li>
          <hr className="dropdown-divider" style={{ borderColor: "rgba(255, 255, 255, 0.1)", margin: "4px 0" }} />
        </li>
        <li>
          <span 
            className="dropdown-item text-white d-flex align-items-center gap-2" 
            style={{ 
              cursor: "pointer",
              backgroundColor: "transparent",
              transition: "background-color 0.3s",
              padding: "8px 16px"
            }} 
            onClick={logoutUser}
            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
          >
            <LogOut size={18} /> Déconnexion
          </span>
        </li>
      </ul>
    </li>
  );

  const TopBar = () => (
    <nav className="navbar navbar-expand-lg shadow-sm" style={{ 
      backgroundColor: "#1B019B", 
      padding: "10px 20px",
      position: "sticky",
      top: 0,
      zIndex: 1030
    }}>
      <div className="container-fluid">
        <Link to="/" className="navbar-brand d-flex align-items-center">
          <img   src={user?.organisation_logo ? `${urlimage}/media/${user.organisation_logo}` : logo}  style={{ 
            width: isMobile ? "80px" : "100px", 
            backgroundColor: "white", 
            borderRadius: "10px", 
            padding: "4px" 
          }} />
        </Link>

        <button 
          className="navbar-toggler border-0" 
          type="button" 
          data-bs-toggle="collapse" 
          data-bs-target="#navbarContent"
          aria-controls="navbarContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
          style={{ color: "white" }}
        >
          <span className="navbar-toggler-icon" />
        </button>

        <div className="collapse navbar-collapse justify-content-end" id="navbarContent">
          <ul className="navbar-nav align-items-center gap-2">
            <li className="nav-item">
              <Link 
                to="/" 
                className={`nav-link text-white fw-semibold d-flex align-items-center gap-2 ${isActive("/") ? "active" : ""}`}
                style={{
                  transition: "background-color 0.3s",
                  borderRadius: "5px",
                  padding: "8px 12px"
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                <Home size={18} /> Accueil
              </Link>
            </li>

            {!token ? (
              <li className="nav-item">
                <Link 
                  to="/login" 
                  className="nav-link text-white fw-semibold d-flex align-items-center gap-2"
                  style={{
                    transition: "background-color 0.3s",
                    borderRadius: "5px",
                    padding: "8px 12px"
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                >
                  <LogIn size={18} /> Connexion
                </Link>
              </li>
            ) : (
              // Afficher les éléments pour les utilisateurs connectés basés sur les permissions
              <>
                {hasPermission("VIEW_MY_PRESENCES") && (
                  <li className="nav-item">
                    <Link 
                      to="/mes-presences" 
                      className={`nav-link text-white fw-semibold d-flex align-items-center gap-2 ${isActive("/mes-presences") ? "active" : ""}`}
                      style={{
                        transition: "background-color 0.3s",
                        borderRadius: "5px",
                        padding: "8px 12px"
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                    >
                      <Clock size={18} /> Mes présences
                    </Link>
                  </li>
                )}
 {hasPermission("VIEW_MY_EVALUATIONS") && (
                  <li className="nav-item">
                    <Link 
                      to="/mes-evaluations" 
                      className={`nav-link text-white fw-semibold d-flex align-items-center gap-2 ${isActive("/mes-evaluations") ? "active" : ""}`}
                      style={{
                        transition: "background-color 0.3s",
                        borderRadius: "5px",
                        padding: "8px 12px"
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                    >
                      <Star size={18} /> Mes évaluations
                    </Link>
                  </li>

)}


                {(hasPermission("REQUEST_LEAVE") || hasPermission("REPORT_LATE")) && (
                  <li className="nav-item dropdown">
                    <span
                      className="nav-link dropdown-toggle text-white fw-semibold d-flex align-items-center gap-2"
                      role="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      style={{
                        cursor: 'pointer',
                        borderRadius: "5px",
                        padding: "8px 12px",
                        transition: "background-color 0.3s",
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                    >
                      <CalendarPlus size={18} /> Faire une demande
                    </span>

                    <ul
                      className="dropdown-menu"
                      style={{
                        backgroundColor: "#1B019B",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "8px",
                        overflow: "hidden",
                        padding: "0",
                      }}
                    >
                      {hasPermission("REQUEST_LEAVE") && (
                        <li>
                          <Link
                            to="/absences"
                            className="dropdown-item d-flex align-items-center gap-2 text-white"
                            style={{
                              padding: "8px 16px",
                              transition: "background-color 0.3s",
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                          >
                            <CalendarPlus size={18} /> Demande d'absence
                          </Link>
                        </li>
                      )}
                      {hasPermission("REPORT_LATE") && (
                        <li>
                          <Link
                            to="/mes-retards"
                            className="dropdown-item d-flex align-items-center gap-2 text-white"
                            style={{
                              padding: "8px 16px",
                              transition: "background-color 0.3s",
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                            onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                          >
                            <Clock size={18} /> Signaler un retard
                          </Link>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
                
                {user && <UserDropdown />}
                {hasPermission("VIEW_NOTIFICATIONS") && <NotificationBell />}
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );

  const SideBar = () => (
    <>
      {isMobile && (
        <button
          className="btn position-fixed"
          style={{
            zIndex: 1050,
            top: "15px",
            left: "15px",
            width: "40px",
            height: "40px",
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1B019B",
            color: "white",
            border: "2px solid white",
          }}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle sidebar"
        >
          {sidebarOpen ? "✕" : "☰"}
        </button>
      )}

      <div
        className="vh-100 p-3 position-fixed overflow-auto"
        style={{
          width: "250px",
          backgroundColor: "#1B019B",
          color: "white",
          top: 0,
          left: isMobile ? (sidebarOpen ? "0" : "-250px") : "0",
          zIndex: 1040,
          boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
          transition: "left 0.3s ease",
        }}
      >
        <div className="navbar-brand mb-4 text-center">
          <img   src={user?.organisation_logo ? `${urlimage}/media/${user.organisation_logo}` : logo}  style={{ 
            width: "140px", 
            padding: "6px", 
            backgroundColor: "white", 
            borderRadius: "10px" 
          }} />
        </div>

        <ul className="nav flex-column gap-2">
          <li className="nav-item">
            <Link 
              className={`nav-link d-flex align-items-center gap-2 ${isActive("/") ? "bg-white text-dark" : "text-white"}`}
              to="/"
              style={{
                padding: "10px 15px",
                borderRadius: "5px",
                transition: "all 0.3s"
              }}
            >
              <Home size={20} /> ACCUEIL
            </Link>
          </li>
          
          {hasPermission("VIEW_DASHBOARD") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/dashboard") ? "bg-white text-dark" : "text-white"}`}
                to="/dashboard"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s"
                }}
              >
                <LayoutDashboard size={20} /> DASHBOARD
              </Link>
            </li>
          )}
          
          {hasPermission("VIEW_PRESENCE") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/presence") ? "bg-white text-dark" : "text-white"}`}
                to="/presence"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s"
                }}
              >
                <Clock size={20} /> PRÉSENCE
              </Link>
            </li>
          )}
          
          {hasPermission("VIEW_EMPLOYE") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/employes") ? "bg-white text-dark" : "text-white"}`}
                to="/employes"
                style={{ padding: "10px 15px", borderRadius: "5px", transition: "all 0.3s" }}
              >
                <Users size={20} /> UTILISATEURS
              </Link>
            </li>
          )}
          
          {hasPermission("VIEW_STATISTICS") && (
            <li className="nav-item">
              <Link
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/statsDetail") ? "bg-white text-dark" : "text-white"}`}
                to="/statsDetail"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s",
                }}
              >
                <CalendarCheck size={20} /> STATISTIQUES
              </Link>
            </li>
          )}

          {hasPermission("VIEW_JUSTIFICATION") && (
            <li className="nav-item">
              <Link
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/justifications") ? "bg-white text-dark" : "text-white"}`}
                to="/justifications"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s",
                }}
              >
                <CalendarCheck size={20} /> JUSTIFICATION
              </Link>
            </li>
          )}

          {hasPermission("VIEW_PENALITE") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/penalites") ? "bg-white text-dark" : "text-white"}`}
                to="/penalites"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s",
                }}
              >
                <AlertTriangle size={20} /> PÉNALITÉS
              </Link>
            </li>
          )}

          {hasPermission("VIEW_PRIME") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/primes") ? "bg-white text-dark" : "text-white"}`}
                to="/primes"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s",
                }}
              >
                <CreditCard size={20} /> PRIMES
              </Link>
            </li>
          )}
{hasPermission("VIEW_OVERTIME_BY_ACTIVITY") && (
  <li className="nav-item">
    <Link 
      className={`nav-link d-flex align-items-center gap-2 ${
        isActive("/heures-sup") ? "bg-white text-dark" : "text-white"
      }`}
      to="/heures-sup"
      style={{
        padding: "10px 15px",
        borderRadius: "5px",
        transition: "all 0.3s",
      }}
    >
      <Clock size={20} /> HEURES SUP
    </Link>
  </li>
)}



          {hasPermission("VIEW_ACTIVITY") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${
                  isActive("/activites") ? "bg-white text-dark" : "text-white"
                }`}
                to="/activites"
                style={{
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s",
                }}
              >
                <ClipboardList size={20} /> ACTIVITÉS
              </Link>
            </li>
          )}

{hasPermission("VIEW_INNOVATION") && (
  <li className="nav-item">
    <Link
      className={`nav-link d-flex align-items-center gap-2 ${
        isActive("/innovations") ? "bg-white text-dark" : "text-white"
      }`}
      to="/innovations"
      style={{
        padding: "10px 15px",
        borderRadius: "5px",
        transition: "all 0.3s",
      }}
    >
      <Lightbulb size={20} /> INNOVATIONS
    </Link>
  </li>
)}


          {hasPermission("VIEW_POINTAGE_MANUEL") && (
            <li className="nav-item">
              <Link
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/pointage-manuels") ? "bg-white text-dark" : "text-white"}`}
                to="/pointage-manuels"
                style={{
                  padding: "10px 10px",
                  borderRadius: "5px",
                  transition: "all 0.3s",
                }}
              >
                <Clipboard size={20} /> POINTAGE MANUELS
              </Link>
            </li>
          )}

          {hasPermission("VIEW_CONFIGURATION") && (
            <li className="nav-item">
              <Link 
                className={`nav-link d-flex align-items-center gap-2 ${isActive("/configuration") ? "bg-white text-dark" : "text-white"}`}
                to="/configuration"
                style={{ padding: "10px 15px", borderRadius: "5px", transition: "all 0.3s" }}
              >
                <Settings size={20} /> CONFIGURATION
              </Link>
            </li>
          )}
        </ul>

        {user && (
          <div className="text-center mb-4 mt-4">
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              overflow: "hidden",
              border: "3px solid white",
              margin: "0 auto"
            }}>
              <img
                src={
                  user.image?.startsWith('/media/')
                    ? `${urlimage}${user.image}`
                    : `${urlimage}/media/${user.image}`
                }
                alt="Profil"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
            <p className="mt-2 fw-semibold">{user.username}</p>

            <div className="dropdown mb-3">
              <button 
                className="btn btn-outline-light dropdown-toggle w-100 d-flex align-items-center justify-content-center gap-2" 
                type="button" 
                id="sidebarUserDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
                style={{
                  padding: "8px",
                  borderRadius: "5px",
                  transition: "all 0.3s"
                }}
              >
                <Settings size={18} /> Mon compte
              </button>
              <ul 
                className="dropdown-menu w-100" 
                aria-labelledby="sidebarUserDropdown"
                style={{
                  backgroundColor: "#1B019B",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  overflow: "hidden"
                }}
              >
                <li>
                  <Link 
                    className="dropdown-item d-flex align-items-center gap-2 text-white" 
                    to="/modifier-compte"
                    style={{
                      padding: "8px 16px",
                      transition: "background-color 0.3s"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                  >
                    <UserPlus size={18} /> Modifier mon compte
                  </Link>
                </li>
                <li>
                  <hr className="dropdown-divider" style={{ borderColor: "rgba(255, 255, 255, 0.1)", margin: "4px 0" }} />
                </li>
                <li>
                  <span 
                    className="dropdown-item d-flex align-items-center gap-2 text-white" 
                    style={{ 
                      cursor: "pointer",
                      padding: "8px 16px",
                      transition: "background-color 0.3s"
                    }} 
                    onClick={logoutUser}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                  >
                    <LogOut size={18} /> Déconnexion
                  </span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {isMobile && sidebarOpen && (
        <div
          className="position-fixed vh-100 w-100"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1035,
            top: 0,
            left: "250px",
            right: 0,
            bottom: 0
          }}
          onClick={() => setSidebarOpen(false)}
          role="button"
          aria-label="Close sidebar"
          tabIndex={0}
        />
      )}
    </>
  );

  // Déterminer quel type de navigation afficher
  const shouldShowSidebar = token && (
    hasPermission("VIEW_DASHBOARD") || 
    hasPermission("VIEW_PRESENCE") || 
    hasPermission("VIEW_EMPLOYE") || 
    hasPermission("VIEW_STATISTICS") || 
    hasPermission("VIEW_JUSTIFICATION") || 
    hasPermission("VIEW_PENALITE") || 
    hasPermission("VIEW_PRIME") || 
    hasPermission("VIEW_POINTAGE_MANUEL") || 
    hasPermission("VIEW_CONFIGURATION")
  );

  return (
    <>
      {!shouldShowSidebar && <TopBar />}
      {shouldShowSidebar && <SideBar />}
      
      {/* Contenu principal - pas de margin-left en mobile */}
      <div 
        className="" 
        style={{ 
          marginLeft: (shouldShowSidebar && !isMobile) ? "250px" : "0",
          transition: "margin-left 0.3s ease",
        }} 
      />
    </>
  );
}

export default Navbar;